import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSiteTitle } from 'src/context/SiteTitleContext';
import { useGetAllEventsForAdminQuery } from '../../../features/events/eventsApiSlice';
import { getSortedEvents } from '../../../lib/Events';
import { filterEvents, getAvailableSections, groupAllEventsByYear, groupEventsByTimePeriod } from './eventBrowserUtils';

/**
 * Custom hook for managing EventBrowser state and logic
 */
export const useEventBrowser = () => {
  const siteTitle = useSiteTitle();
  const auth = useAuth();

  const { data: events = [] } = useGetAllEventsForAdminQuery(auth.user?.access_token ?? '');

  const [filterText, setFilterText] = useState('');

  // State for collapsible sections
  const [sectionsExpanded, setSectionsExpanded] = useState({
    recent: false,
    lastYear: false,
    older: false,
  });

  // Set the page title, and scroll to the top of the page when the page is loaded.
  useEffect(() => {
    document.title = `${siteTitle.title} - Event administration`;
    window.scrollTo(0, 0);
  }, [siteTitle.title]);

  // Assign the base list of sorted events when new result is received from the API.
  const sortedEvents = useMemo(() => {
    if (events === undefined || events.length === 0) {
      return [];
    }
    return getSortedEvents(events);
  }, [events]);

  // Assign the subset of events to display in the component based on whether there is a filter or not present.
  const filteredEvents = useMemo(() => {
    if (!filterText || filterText.length === 0) {
      return sortedEvents;
    } else {
      return filterEvents(sortedEvents, filterText);
    }
  }, [filterText, sortedEvents]);

  // Group filtered events by time periods
  const eventsByTimePeriod = useMemo(() => {
    return groupEventsByTimePeriod(filteredEvents);
  }, [filteredEvents]);

  // Calculate total number of events
  const totalEvents = useMemo(() => {
    const recentCount = eventsByTimePeriod.recent.length;
    const lastYearCount = eventsByTimePeriod.lastYear.length;
    const olderCount = Object.values(eventsByTimePeriod.older).reduce((sum, events) => sum + events.length, 0);
    return recentCount + lastYearCount + olderCount;
  }, [eventsByTimePeriod]);

  // Determine if we should use collapsible sections (>10 events)
  const useCollapsibleSections = totalEvents > 10;

  // Group events by year for non-collapsible view
  const eventsByYear = useMemo(() => {
    return groupAllEventsByYear(eventsByTimePeriod);
  }, [eventsByTimePeriod]);

  // Determine which sections have content and set initial expanded state
  const availableSections = useMemo(() => {
    return getAvailableSections(eventsByTimePeriod);
  }, [eventsByTimePeriod]);

  // Set the first available section as expanded when sections change
  useEffect(() => {
    const newExpanded = {
      recent: false,
      lastYear: false,
      older: false,
    };

    if (availableSections.length > 0) {
      newExpanded[availableSections[0] as keyof typeof newExpanded] = true;
    }

    setSectionsExpanded(newExpanded);
  }, [availableSections]);

  const toggleSection = useCallback(
    (section: 'recent' | 'lastYear' | 'older') => {
      setSectionsExpanded((prev) => {
        if (useCollapsibleSections) {
          // If using collapsible sections, only allow one section to be open at a time
          const newExpanded = {
            recent: false,
            lastYear: false,
            older: false,
          };
          // If the section is currently closed, open it; if it's open, close it
          newExpanded[section] = !prev[section];
          return newExpanded;
        } else {
          // If not using collapsible sections, just toggle normally (though this shouldn't be called)
          return {
            ...prev,
            [section]: !prev[section],
          };
        }
      });
    },
    [useCollapsibleSections]
  );

  const handleFilterChange = useCallback((newFilterText: string) => {
    setFilterText(newFilterText);
  }, []);

  return {
    eventsByTimePeriod,
    sectionsExpanded,
    toggleSection,
    handleFilterChange,
    useCollapsibleSections,
    totalEvents,
    eventsByYear,
  };
};
